import { useFeatureAccess } from "../../../access-control/hooks/useFeatureAccess";
import { FEATURE_KEYS } from "../../../access-control/models/role-feature.models";
import { ImportIcon } from "../../../shared/Icons";
import { themeAtom } from "../../../shared/atoms";
import LoadingWrapper from "../../../shared/components/LoadingWrapper";
import { log, logE, logV } from "../../../shared/services/debug-logger";
import { useAdminCrewReportBulkRequests } from "../../hooks/useAdminCrewReportBulkRequests";
import type {
  AdminCrewReportImportRunResult,
  AdminCrewReportImportPreview,
  AdminCrewReportImportStatus,
  AdminCrewReportRecipientResolutionStatus,
  AdminCrewReportRequestConfirmationTarget,
  AdminCrewReportRequestMessageStatus,
  AdminCrewReportRequestStatus,
  AdminCrewReportRunningVesselCrewReport,
  AdminCrewReportStatisticsResponse,
  AdminCrewReportStatisticsStatus,
  AdminCrewReportUploadPreview,
  AdminCrewReportVesselOption,
  AdminCrewReportVesselsStatus,
  AdminCrewReportUploadStatus,
  AdminCrewReportUploadSubmitStatus,
  AdminCrewReportVesselDetailsResponse,
  AdminCrewReportVesselDetailsStatus,
} from "../../models/admin-crew-report.models";
import {
  getAdminCrewReportUploadOptions,
  getAdminCrewReportVesselDetails,
  getAdminCrewReportVessels,
  getAdminCrewReportImportPreview,
  getAdminCrewReportStatistics,
  importAdminCrewReports,
  requestAdminCrewReport,
  resolveAdminCrewReportRecipient,
  submitAdminCrewReportUpload,
} from "../../services/admin-crew-report.service";
import { adminStyles } from "../../styles/admin.styles";
import {
  executeAdminCrewReportImportRun,
  getAdminCrewReportImportRunSummaryMessage,
  printAdminCrewReportImportRunReport,
} from "../../utils/admin-crew-report-import.utils";
import { toAdminCrewReportVesselOption } from "../../utils/admin-crew-report-running-vessels.utils";
import { getAdminCrewReportImoFromFileName } from "../../utils/admin-crew-report-workbook-layout.utils";
import { ADMIN_CREW_REPORT_ACTIONS_FORM_ID } from "../../utils/admin-crew-report.constants";
import {
  applyAdminCrewReportUploadSelectEditors,
  getAdminCrewReportErrorDetails,
  getAdminCrewReportErrorMessage,
  getAdminCrewReportRequestFeedback,
  getAdminCrewReportUploadErrorMessage,
  getCrewReportRequestButtonLabel,
  parseAdminCrewReportUploadFile,
  toAdminCrewReportUploadPayloadRows,
} from "../../utils/admin-crew-report.utils";
import AdminCrewReportBulkRequestConfirmationModal from "./AdminCrewReportBulkRequestConfirmationModal";
import AdminCrewReportBulkRequestStatusModal from "./AdminCrewReportBulkRequestStatusModal";
import AdminCrewReportCheckbox from "./AdminCrewReportCheckbox";
import AdminCrewReportImportConfirmationModal from "./AdminCrewReportImportConfirmationModal";
import AdminCrewReportImportModal from "./AdminCrewReportImportModal";
import AdminCrewReportRequestConfirmationModal from "./AdminCrewReportRequestConfirmationModal";
import AdminCrewReportRunningVesselsTable from "./AdminCrewReportRunningVesselsTable";
import AdminCrewReportStatisticsGrid from "./AdminCrewReportStatisticsGrid";
import AdminCrewReportUploadModal from "./AdminCrewReportUploadModal";
import AdminCrewReportVesselSelector from "./AdminCrewReportVesselSelector";
import AdminCrewReportViewModal from "./AdminCrewReportViewModal";
import { ArrowUpTrayIcon, ChevronDownIcon, PaperAirplaneIcon } from "@heroicons/react/24/outline";
import { useAtomValue } from "jotai";
import type { ChangeEvent, FormEvent } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";

const AdminCrewReportPanel = () => {
  const theme = useAtomValue(themeAtom);
  const { hasFeature } = useFeatureAccess();
  const canImportCrewReports = hasFeature(FEATURE_KEYS.CREW_REPORT_IMPORT_ACTIONS);
  const canUseOperationalActions = canImportCrewReports || hasFeature(FEATURE_KEYS.CREW_REPORT_OPERATIONAL_ACTIONS);
  const canUseBulkRequests = hasFeature(FEATURE_KEYS.CREW_REPORT_BULK_REQUESTS);
  const uploadInputRef = useRef<HTMLInputElement | null>(null);
  const statisticsRequestIdRef = useRef(0);
  const statisticsRef = useRef<AdminCrewReportStatisticsResponse | null>(null);
  const bulkStatusRefreshPendingRef = useRef(false);
  const [isCrewReportActionsExpanded, setIsCrewReportActionsExpanded] = useState(false);
  const [vessels, setVessels] = useState<AdminCrewReportVesselOption[]>([]);
  const [vesselsStatus, setVesselsStatus] = useState<AdminCrewReportVesselsStatus>("loading");
  const [vesselLoadMessage, setVesselLoadMessage] = useState("");
  const [selectedVessel, setSelectedVessel] = useState<AdminCrewReportVesselOption>();
  const [recipientStatus, setRecipientStatus] = useState<AdminCrewReportRecipientResolutionStatus>("idle");
  const [recipientMessage, setRecipientMessage] = useState("");
  const [resolvedRecipientEmail, setResolvedRecipientEmail] = useState("");
  const [status, setStatus] = useState<AdminCrewReportRequestStatus>("idle");
  const [statusMessage, setStatusMessage] = useState("");
  const [uploadStatus, setUploadStatus] = useState<AdminCrewReportUploadStatus>("idle");
  const [uploadStatusMessage, setUploadStatusMessage] = useState("");
  const [uploadPreview, setUploadPreview] = useState<AdminCrewReportUploadPreview | null>(null);
  const [uploadSubmitStatus, setUploadSubmitStatus] = useState<AdminCrewReportUploadSubmitStatus>("idle");
  const [uploadSubmitMessage, setUploadSubmitMessage] = useState("");
  const [uploadSubmitMessageDetails, setUploadSubmitMessageDetails] = useState<string[]>([]);
  const [uploadVesselDetailsStatus, setUploadVesselDetailsStatus] =
    useState<AdminCrewReportVesselDetailsStatus>("idle");
  const [uploadVesselDetails, setUploadVesselDetails] = useState<AdminCrewReportVesselDetailsResponse | null>(null);
  const [importStatus, setImportStatus] = useState<AdminCrewReportImportStatus>("idle");
  const [importStatusMessage, setImportStatusMessage] = useState("");
  const [importRunResult, setImportRunResult] = useState<AdminCrewReportImportRunResult | null>(null);
  const [importPreview, setImportPreview] = useState<AdminCrewReportImportPreview | null>(null);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [requestConfirmationTarget, setRequestConfirmationTarget] =
    useState<AdminCrewReportRequestConfirmationTarget | null>(null);
  const [activeTableRequestImoNumber, setActiveTableRequestImoNumber] = useState<string | null>(null);
  const [tableRequestMessage, setTableRequestMessage] = useState("");
  const [tableRequestMessageStatus, setTableRequestMessageStatus] =
    useState<AdminCrewReportRequestMessageStatus>("sent");
  const [viewReportVessel, setViewReportVessel] = useState<AdminCrewReportRunningVesselCrewReport | null>(null);
  const [allowTokenlessInitialImports, setAllowTokenlessInitialImports] = useState(false);
  const [statisticsStatus, setStatisticsStatus] = useState<AdminCrewReportStatisticsStatus>("loading");
  const [statistics, setStatistics] = useState<AdminCrewReportStatisticsResponse | null>(null);
  const [statisticsErrorMessage, setStatisticsErrorMessage] = useState("");
  const [statisticsRefreshVersion, setStatisticsRefreshVersion] = useState(0);
  const [areStatisticsRequestActionsStale, setAreStatisticsRequestActionsStale] = useState(false);
  const [isBulkStatusRefreshPending, setIsBulkStatusRefreshPending] = useState(false);
  const [selectedBulkRequestImoNumbers, setSelectedBulkRequestImoNumbers] = useState<Set<string>>(new Set());
  const handleBulkRequestAttemptCompleted = useCallback(
    (imoNumber: string, lifecycleStatus?: AdminCrewReportRunningVesselCrewReport["requestStatus"]) => {
      if (lifecycleStatus) {
        setStatistics((currentStatistics) => {
          if (!currentStatistics) return currentStatistics;

          const nextStatistics = {
            ...currentStatistics,
            runningVesselCrewReports: {
              ...currentStatistics.runningVesselCrewReports,
              vessels: currentStatistics.runningVesselCrewReports.vessels.map((vessel) =>
                vessel.imoNumber === imoNumber ? { ...vessel, requestStatus: lifecycleStatus } : vessel,
              ),
            },
          };
          statisticsRef.current = nextStatistics;
          return nextStatistics;
        });
      }
    },
    [],
  );
  const handleBulkRequestRunSettled = useCallback(() => {
    log("AdminCrewReportPanel - handleBulkRequestRunSettled - refresh");
    bulkStatusRefreshPendingRef.current = true;
    setIsBulkStatusRefreshPending(true);
    setTableRequestMessage("Updating Crew Report request statuses.");
    setTableRequestMessageStatus("sent");
    statisticsRequestIdRef.current += 1;
    setStatisticsRefreshVersion((version) => version + 1);
  }, []);
  const clearBulkRequestSelection = useCallback(() => setSelectedBulkRequestImoNumbers(new Set()), []);
  const bulkRequests = useAdminCrewReportBulkRequests({
    onAttemptCompleted: handleBulkRequestAttemptCompleted,
    onRunSettled: handleBulkRequestRunSettled,
    onStatusModalClosed: clearBulkRequestSelection,
  });
  const isSending = status === "sending";
  const isParsingUpload = uploadStatus === "parsing";
  const isSavingUpload = uploadSubmitStatus === "saving";
  const isLoadingVessels = vesselsStatus === "loading";
  const isImporting = importStatus === "importing";
  const isPreviewingImport = importStatus === "previewing";
  const isConfirmingImport = importStatus === "confirming";
  const isImportLoading = isPreviewingImport || isImporting;
  const isImportBusy = isImporting || isPreviewingImport || isConfirmingImport;
  const isInitialStatisticsLoading = statisticsStatus === "loading" && !statistics;
  const isPageLoading = isInitialStatisticsLoading || (canImportCrewReports && isImportLoading);
  const uploadFileVesselImo = uploadPreview ? getAdminCrewReportImoFromFileName(uploadPreview.fileName) : "";
  const isUploadVesselIdentityResolving =
    Boolean(uploadFileVesselImo) &&
    (uploadVesselDetailsStatus === "idle" ||
      uploadVesselDetailsStatus === "resolving" ||
      (uploadVesselDetailsStatus === "resolved" && uploadVesselDetails?.imoNumber !== uploadFileVesselImo));
  const isUploadVesselIdentityUnresolved =
    uploadVesselDetailsStatus === "unresolved" || uploadVesselDetailsStatus === "error";
  const uploadSubmitDisabledReason = !uploadFileVesselImo
    ? "Rename the file to the vessel’s seven-digit IMO followed by .xlsx, for example 9713832.xlsx."
    : isUploadVesselIdentityResolving
      ? "Resolving vessel details before submitting."
      : isUploadVesselIdentityUnresolved
        ? "The IMO in the filename was not recognized. Check the filename before submitting."
        : undefined;
  const canRequestCrewReport =
    canUseOperationalActions &&
    Boolean(selectedVessel) &&
    recipientStatus === "resolved" &&
    Boolean(resolvedRecipientEmail) &&
    !isSending &&
    !isImportBusy &&
    !bulkRequests.isWorkflowActive &&
    !requestConfirmationTarget &&
    !activeTableRequestImoNumber;
  const isSingleRequestWorkflowBusy =
    isSending ||
    recipientStatus === "resolving" ||
    Boolean(activeTableRequestImoNumber) ||
    Boolean(requestConfirmationTarget);
  const isRequestWorkflowBusy = isSingleRequestWorkflowBusy || bulkRequests.isWorkflowActive;
  const tableReportActionsDisabled = isParsingUpload || isImportBusy || isRequestWorkflowBusy;
  const tableRequestActionsDisabled = tableReportActionsDisabled || areStatisticsRequestActionsStale;
  const tableBulkRequestControlsDisabled =
    isParsingUpload || isImportBusy || isSingleRequestWorkflowBusy || areStatisticsRequestActionsStale;
  const tableRequestActionBusy =
    Boolean(activeTableRequestImoNumber) &&
    (isSending || !requestConfirmationTarget || requestConfirmationTarget.origin !== "table");
  const requestButtonLabel = getCrewReportRequestButtonLabel(isSending, recipientStatus);
  const uploadInputLabel = "Upload Crew Report Excel file";
  const recipientStatusMessageClassName = twMerge(
    "text-sm font-medium",
    recipientStatus === "resolved" ? "text-green" : "text-[#BF0002]",
  );
  const uploadStatusMessageClassName = twMerge(
    "text-sm font-medium",
    uploadStatus === "error" ? "text-[#BF0002]" : "text-green",
  );
  const requestStatusMessageClassName = twMerge(
    "text-sm font-medium",
    status === "sent"
      ? "text-green"
      : status === "sentWithWarning"
        ? "text-[#8A5A00] dark:text-amber-300"
        : "text-[#BF0002]",
  );
  const importStatusMessageClassName = twMerge(
    "text-sm font-medium",
    importStatus === "error" || (importRunResult && importRunResult.outcome !== "COMPLETED")
      ? "text-[#BF0002]"
      : "text-green",
  );

  useEffect(() => {
    if (!canUseOperationalActions) {
      setVessels([]);
      setVesselsStatus("loaded");
      setVesselLoadMessage("");
      return;
    }

    let isCurrentRequest = true;

    const loadCrewReportVessels = async () => {
      setVesselsStatus("loading");
      setVesselLoadMessage("");

      try {
        const response = await getAdminCrewReportVessels();

        if (!isCurrentRequest) {
          return;
        }

        setVessels(response);
        setVesselsStatus("loaded");
      } catch (error) {
        if (!isCurrentRequest) {
          return;
        }

        setVessels([]);
        setVesselsStatus("error");
        setVesselLoadMessage(getAdminCrewReportErrorMessage(error));
      }
    };

    loadCrewReportVessels();

    return () => {
      isCurrentRequest = false;
    };
  }, [canUseOperationalActions]);

  useEffect(() => {
    const requestId = statisticsRequestIdRef.current + 1;
    statisticsRequestIdRef.current = requestId;
    setStatisticsErrorMessage("");
    if (!statisticsRef.current) {
      setStatisticsStatus("loading");
    }

    const loadCrewReportStatistics = async () => {
      try {
        const response = await getAdminCrewReportStatistics();

        if (statisticsRequestIdRef.current !== requestId) {
          return;
        }

        statisticsRef.current = response;
        setStatistics(response);
        setStatisticsStatus("loaded");
        setAreStatisticsRequestActionsStale(false);
        if (bulkStatusRefreshPendingRef.current) {
          bulkStatusRefreshPendingRef.current = false;
          setIsBulkStatusRefreshPending(false);
          setTableRequestMessage("");
        }
      } catch (error) {
        if (statisticsRequestIdRef.current !== requestId) {
          return;
        }

        const errorMessage = getAdminCrewReportErrorMessage(error);
        setStatisticsStatus(statisticsRef.current ? "loaded" : "error");
        setStatisticsErrorMessage(errorMessage);
        if (bulkStatusRefreshPendingRef.current) {
          bulkStatusRefreshPendingRef.current = false;
          setIsBulkStatusRefreshPending(false);
          setTableRequestMessage(errorMessage);
          setTableRequestMessageStatus("error");
        }
      }
    };

    loadCrewReportStatistics();

    return () => {
      if (statisticsRequestIdRef.current === requestId) {
        statisticsRequestIdRef.current += 1;
      }
    };
  }, [statisticsRefreshVersion]);

  useEffect(() => {
    if (!canUseBulkRequests) {
      setSelectedBulkRequestImoNumbers((currentSelection) =>
        currentSelection.size === 0 ? currentSelection : new Set(),
      );
      return;
    }

    const availableImoNumbers = new Set(
      statistics?.runningVesselCrewReports.vessels.map((vessel) => vessel.imoNumber) ?? [],
    );
    setSelectedBulkRequestImoNumbers((currentSelection) => {
      const nextSelection = new Set(
        Array.from(currentSelection).filter((imoNumber) => availableImoNumbers.has(imoNumber)),
      );
      return nextSelection.size === currentSelection.size ? currentSelection : nextSelection;
    });
  }, [canUseBulkRequests, statistics?.runningVesselCrewReports.vessels]);

  useEffect(() => {
    if (!canUseOperationalActions || !selectedVessel) {
      setRecipientStatus("idle");
      setRecipientMessage("");
      setResolvedRecipientEmail("");
      return;
    }

    let isCurrentRequest = true;

    const resolveRecipient = async () => {
      setRecipientStatus("resolving");
      setRecipientMessage("");
      setResolvedRecipientEmail("");

      try {
        const response = await resolveAdminCrewReportRecipient(selectedVessel);

        if (!isCurrentRequest) {
          return;
        }

        setRecipientStatus("resolved");
        setResolvedRecipientEmail(response.email);
        setRecipientMessage(`Recipient email resolved for ${response.vesselName}: ${response.email}.`);
      } catch (error) {
        if (!isCurrentRequest) {
          return;
        }

        setRecipientStatus("error");
        setResolvedRecipientEmail("");
        setRecipientMessage(getAdminCrewReportErrorMessage(error));
      }
    };

    resolveRecipient();

    return () => {
      isCurrentRequest = false;
    };
  }, [canUseOperationalActions, selectedVessel]);

  useEffect(() => {
    if (!canUseOperationalActions || !uploadFileVesselImo) {
      setUploadVesselDetails(null);
      setUploadVesselDetailsStatus("idle");
      return;
    }

    let isCurrentRequest = true;

    const loadUploadVesselDetails = async () => {
      setUploadVesselDetails(null);
      setUploadVesselDetailsStatus("resolving");

      try {
        const response = await getAdminCrewReportVesselDetails(uploadFileVesselImo);

        if (!isCurrentRequest) {
          return;
        }

        setUploadVesselDetails(response);
        setUploadVesselDetailsStatus(response.recognized ? "resolved" : "unresolved");
      } catch (_error) {
        if (!isCurrentRequest) {
          return;
        }

        setUploadVesselDetails({ ok: true, recognized: false, imoNumber: uploadFileVesselImo });
        setUploadVesselDetailsStatus("error");
      }
    };

    loadUploadVesselDetails();

    return () => {
      isCurrentRequest = false;
    };
  }, [canUseOperationalActions, uploadFileVesselImo]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!canUseOperationalActions || !selectedVessel || recipientStatus !== "resolved" || !resolvedRecipientEmail) {
      log("AdminCrewReportPanel - handleSubmit - blocked", { recipientStatus });
      setStatus("error");
      setStatusMessage("Select a vessel with a resolved recipient email.");
      return;
    }

    log("AdminCrewReportPanel - handleSubmit - confirmation");
    setRequestConfirmationTarget({
      origin: "selector",
      vessel: selectedVessel,
      recipientEmail: resolvedRecipientEmail,
    });
  };

  const closeRequestConfirmation = () => {
    log("AdminCrewReportPanel - closeRequestConfirmation");
    if (requestConfirmationTarget?.origin === "table") {
      setActiveTableRequestImoNumber(null);
    }
    setRequestConfirmationTarget(null);
  };

  const handleConfirmCrewReportRequest = async () => {
    const target = requestConfirmationTarget;
    if (!canUseOperationalActions || !target || isSending) {
      log("AdminCrewReportPanel - handleConfirmCrewReportRequest - blocked", { pending: isSending });
      setRequestConfirmationTarget(null);
      setStatus("error");
      setStatusMessage("Select a vessel with a resolved recipient email.");
      return;
    }

    log("AdminCrewReportPanel - handleConfirmCrewReportRequest", { origin: target.origin });
    setRequestConfirmationTarget(null);
    setStatus("sending");
    setStatusMessage("");
    if (target.origin === "table") {
      setTableRequestMessage("");
      setActiveTableRequestImoNumber(target.vessel.imoNumber);
    }

    try {
      const response = await requestAdminCrewReport({
        vesselCode: target.vessel.vesselCode,
        imoNumber: target.vessel.imoNumber,
      });
      const feedback = getAdminCrewReportRequestFeedback(response);
      log("AdminCrewReportPanel - handleConfirmCrewReportRequest - applied", { status: feedback.status });
      statisticsRequestIdRef.current += 1;
      setStatisticsRefreshVersion((version) => version + 1);

      if (target.origin === "table") {
        setStatus(feedback.status);
        setTableRequestMessageStatus(feedback.status);
        setTableRequestMessage(feedback.message);
      } else {
        setStatus(feedback.status);
        setStatusMessage(feedback.message);
      }
    } catch (error) {
      logE(error, { operation: "AdminCrewReportPanel - handleConfirmCrewReportRequest - failed" });
      const message = getAdminCrewReportErrorMessage(error);
      if (target.origin === "table") {
        setStatus("error");
        setTableRequestMessageStatus("error");
        setTableRequestMessage(message);
      } else {
        setStatus("error");
        setStatusMessage(message);
      }
    } finally {
      if (target.origin === "table") {
        setActiveTableRequestImoNumber(null);
      }
    }
  };

  const handleTableRequestReport = async (vessel: AdminCrewReportRunningVesselCrewReport) => {
    if (!canUseOperationalActions || tableRequestActionsDisabled) {
      log("AdminCrewReportPanel - handleTableRequestReport - blocked");
      return;
    }

    log("AdminCrewReportPanel - handleTableRequestReport");
    const requestVessel = toAdminCrewReportVesselOption(vessel);
    setActiveTableRequestImoNumber(vessel.imoNumber);
    setTableRequestMessage("");

    try {
      const response = await resolveAdminCrewReportRecipient(requestVessel);
      log("AdminCrewReportPanel - handleTableRequestReport - confirmation");
      setRequestConfirmationTarget({
        origin: "table",
        vessel: requestVessel,
        recipientEmail: response.email,
      });
    } catch (error) {
      logE(error, { operation: "AdminCrewReportPanel - handleTableRequestReport - failed" });
      setTableRequestMessageStatus("error");
      setTableRequestMessage(getAdminCrewReportErrorMessage(error));
      setActiveTableRequestImoNumber(null);
    }
  };

  const handleTableShowReport = (vessel: AdminCrewReportRunningVesselCrewReport) => {
    if (tableReportActionsDisabled) return;

    setViewReportVessel(vessel);
  };

  const handleUploadButtonClick = () => {
    if (!canUseOperationalActions || isImportBusy || isRequestWorkflowBusy) {
      log("AdminCrewReportPanel - handleUploadButtonClick - blocked");
      return;
    }

    log("AdminCrewReportPanel - handleUploadButtonClick");
    uploadInputRef.current?.click();
  };

  const handleUploadFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";

    if (!canUseOperationalActions || !file || isRequestWorkflowBusy) {
      log("AdminCrewReportPanel - handleUploadFileChange - skipped", { hasFile: Boolean(file) });
      return;
    }

    log("AdminCrewReportPanel - handleUploadFileChange");
    setUploadStatus("parsing");
    setUploadStatusMessage(`Reading ${file.name}.`);
    setUploadPreview(null);
    setUploadSubmitStatus("idle");
    setUploadSubmitMessage("");
    setUploadSubmitMessageDetails([]);

    try {
      const [preview, uploadOptions] = await Promise.all([
        parseAdminCrewReportUploadFile(file),
        getAdminCrewReportUploadOptions(),
      ]);
      const previewWithSelectEditors = applyAdminCrewReportUploadSelectEditors(preview, uploadOptions);
      log("AdminCrewReportPanel - handleUploadFileChange - applied", { count: previewWithSelectEditors.rows.length });

      setUploadPreview(previewWithSelectEditors);
      setUploadStatus("ready");
      setUploadStatusMessage(
        `Loaded ${previewWithSelectEditors.rows.length} rows from ${previewWithSelectEditors.fileName}.`,
      );
    } catch (error) {
      logE(error, { operation: "AdminCrewReportPanel - handleUploadFileChange - failed" });
      setUploadStatus("error");
      setUploadStatusMessage(getAdminCrewReportUploadErrorMessage(error));
      setUploadPreview(null);
    }
  };

  const closeUploadPreview = () => {
    if (isSavingUpload) {
      return;
    }

    log("AdminCrewReportPanel - closeUploadPreview");
    setUploadPreview(null);
    setUploadStatus("idle");
    setUploadStatusMessage("");
    setUploadSubmitStatus("idle");
    setUploadSubmitMessage("");
    setUploadSubmitMessageDetails([]);
    setUploadVesselDetails(null);
    setUploadVesselDetailsStatus("idle");
  };

  const handleUploadRowsChange = (rows: AdminCrewReportUploadPreview["rows"]) => {
    if (isSavingUpload) {
      return;
    }

    setUploadPreview((current) => (current ? { ...current, rows } : current));

    if (uploadSubmitStatus === "error") {
      setUploadSubmitStatus("idle");
      setUploadSubmitMessage("");
      setUploadSubmitMessageDetails([]);
    }
  };

  const handleUploadSubmit = async () => {
    if (!canUseOperationalActions || !uploadPreview || isSavingUpload) {
      log("AdminCrewReportPanel - handleUploadSubmit - blocked", { pending: isSavingUpload });
      return;
    }

    if (uploadSubmitDisabledReason) {
      log("AdminCrewReportPanel - handleUploadSubmit - unresolved vessel", { status: uploadVesselDetailsStatus });
      setUploadSubmitStatus("error");
      setUploadSubmitMessage(uploadSubmitDisabledReason);
      setUploadSubmitMessageDetails([]);
      return;
    }

    log("AdminCrewReportPanel - handleUploadSubmit", { count: uploadPreview.rows.length });
    setUploadSubmitStatus("saving");
    setUploadSubmitMessage("");
    setUploadSubmitMessageDetails([]);

    try {
      const response = await submitAdminCrewReportUpload({
        fileName: uploadPreview.fileName,
        rows: toAdminCrewReportUploadPayloadRows(uploadPreview.rows),
      });
      log("AdminCrewReportPanel - handleUploadSubmit - applied", { count: response.savedRows });

      setUploadPreview(null);
      setUploadVesselDetails(null);
      setUploadVesselDetailsStatus("idle");
      setUploadSubmitStatus("idle");
      setUploadSubmitMessage("");
      setUploadSubmitMessageDetails([]);
      setUploadStatus("ready");
      setUploadStatusMessage(`Saved ${response.savedRows} crew rows for IMO ${response.imoNumber}.`);
      statisticsRequestIdRef.current += 1;
      setAreStatisticsRequestActionsStale(true);
      setStatisticsRefreshVersion((version) => version + 1);
    } catch (error) {
      logE(error, { operation: "AdminCrewReportPanel - handleUploadSubmit - failed" });
      setUploadSubmitStatus("error");
      setUploadSubmitMessage(getAdminCrewReportErrorMessage(error));
      setUploadSubmitMessageDetails(getAdminCrewReportErrorDetails(error));
    }
  };

  const handleImportPreview = async () => {
    if (!canImportCrewReports || isImportBusy || isRequestWorkflowBusy || isParsingUpload || isSavingUpload) {
      log("AdminCrewReportPanel - handleImportPreview - blocked");
      return;
    }

    log("AdminCrewReportPanel - handleImportPreview");
    setImportStatus("previewing");
    setImportPreview(null);
    setImportRunResult(null);
    setImportStatusMessage("Checking eligible Crew Report emails.");

    try {
      const preview = await getAdminCrewReportImportPreview(allowTokenlessInitialImports);
      log("AdminCrewReportPanel - handleImportPreview - applied", {
        count: preview.candidateEmailCount,
        hasMore: preview.hasMore,
      });
      setImportPreview(preview);
      setImportStatus("confirming");
      setImportStatusMessage("");
    } catch (error) {
      logE(error, { operation: "AdminCrewReportPanel - handleImportPreview - failed" });
      setImportStatus("error");
      setImportStatusMessage(getAdminCrewReportErrorMessage(error));
    }
  };

  const closeImportPreview = () => {
    log("AdminCrewReportPanel - closeImportPreview");
    setImportPreview(null);
    setImportStatus("idle");
    setImportStatusMessage("");
  };

  const handleImportCrewReports = async () => {
    if (
      !canImportCrewReports ||
      !importPreview ||
      !isConfirmingImport ||
      isSending ||
      isParsingUpload ||
      isSavingUpload
    ) {
      log("AdminCrewReportPanel - handleImportCrewReports - blocked", { status: importStatus });
      return;
    }

    const confirmedPreview = importPreview;
    log("AdminCrewReportPanel - handleImportCrewReports", {
      broaderImport: confirmedPreview.allowTokenlessInitialImports,
    });
    setImportPreview(null);
    setImportStatus("importing");
    setImportRunResult(null);
    setImportStatusMessage(
      confirmedPreview.allowTokenlessInitialImports
        ? "Checking eligible requested replies and broader authenticated Crew Report emails, " +
            "including unfinished recovery windows."
        : "Checking eligible requested Crew Report replies, including unfinished recovery windows.",
    );

    try {
      const result = await executeAdminCrewReportImportRun(
        confirmedPreview.allowTokenlessInitialImports,
        importAdminCrewReports,
        confirmedPreview.runCutoffAt,
      );
      log("AdminCrewReportPanel - handleImportCrewReports - applied", {
        outcome: result.outcome,
        imported: result.counts.imported,
        failed: result.counts.failed,
      });
      if (result.continuationPending || result.retryableItemsPending || result.interruption) {
        log("AdminCrewReportPanel - handleImportCrewReports - unfinished", {
          continuation: result.continuationPending,
          retryable: result.retryableItemsPending,
          interrupted: Boolean(result.interruption),
        });
      }
      statisticsRequestIdRef.current += 1;
      if (result.counts.imported > 0) {
        setAreStatisticsRequestActionsStale(true);
      }
      setStatisticsRefreshVersion((version) => version + 1);
      setImportRunResult(result);
      setImportStatus(result.outcome === "FAILED" ? "error" : "completed");
      setImportStatusMessage(getAdminCrewReportImportRunSummaryMessage(result));
      setIsImportModalOpen(true);
    } catch (error) {
      logE(error, { operation: "AdminCrewReportPanel - handleImportCrewReports - failed" });
      setImportStatus("error");
      setImportStatusMessage(getAdminCrewReportErrorMessage(error));
    } finally {
      setAllowTokenlessInitialImports(false);
    }
  };

  logV("AdminCrewReportPanel - BUILD", { loading: isPageLoading, uploadStatus, importStatus });

  const busyPage = (
    <main
      data-test="admin-crew-report-page"
      aria-busy={isPageLoading}
      className={twMerge("flex min-h-0 min-w-0 flex-1 overflow-hidden", theme.home.tailwind.textDefault)}
    >
      {isPageLoading && (
        <div data-testid="admin-crew-report-loading-layer" className="fixed inset-0 z-40">
          <LoadingWrapper isLoading loadingIndicatorClassName={theme.home.tailwind.spinner} loadingIndicatorSize="lg" />
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-y-auto bg-[#F8FBFA] p-6 dark:bg-gray-900">
        <div className="flex w-full flex-col gap-5">
          <header className="flex items-center justify-between gap-4 bg-white px-5 py-4 shadow-sm dark:bg-gray-800">
            <div>
              <p className={adminStyles.heading.eyebrow}>Admin</p>
              <h1 className={adminStyles.heading.pageTitle}>Crew Report</h1>
            </div>

            {canUseOperationalActions && (
              <button
                type="button"
                aria-label={isCrewReportActionsExpanded ? "Collapse Crew Report actions" : "Expand Crew Report actions"}
                aria-controls={ADMIN_CREW_REPORT_ACTIONS_FORM_ID}
                aria-expanded={isCrewReportActionsExpanded}
                data-test="admin-crew-report-actions-toggle"
                className="flex h-9 flex-shrink-0 items-center justify-center gap-2 whitespace-nowrap px-2 text-xs font-medium text-green transition-colors hover:bg-primary-10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green disabled:cursor-not-allowed disabled:opacity-50"
                disabled={isPageLoading}
                onClick={() => setIsCrewReportActionsExpanded((isExpanded) => !isExpanded)}
              >
                <span>Operational actions</span>
                <ChevronDownIcon
                  aria-hidden="true"
                  className={twMerge(
                    "h-5 w-5 flex-none transition-transform",
                    isCrewReportActionsExpanded && "rotate-180",
                  )}
                />
              </button>
            )}
          </header>

          <form
            id={ADMIN_CREW_REPORT_ACTIONS_FORM_ID}
            data-test="admin-crew-report-form"
            data-testid="admin-crew-report-form"
            hidden={!canUseOperationalActions || !isCrewReportActionsExpanded}
            className={twMerge(
              "grid gap-4 bg-white px-5 py-5 shadow-sm dark:bg-gray-800",
              (!canUseOperationalActions || !isCrewReportActionsExpanded) && "hidden",
            )}
            onSubmit={handleSubmit}
          >
            <div className="flex min-w-0 flex-wrap items-start gap-3 xl:flex-nowrap">
              <div className="min-w-[260px] max-w-[690px] flex-[1_1_510px]">
                <AdminCrewReportVesselSelector
                  vessels={vessels}
                  selectedVessel={selectedVessel}
                  loading={isLoadingVessels}
                  disabled={
                    isSending ||
                    isImportBusy ||
                    Boolean(activeTableRequestImoNumber) ||
                    Boolean(requestConfirmationTarget)
                  }
                  onSelectVessel={(vessel) => {
                    setSelectedVessel(vessel);
                    setRecipientStatus(vessel ? "resolving" : "idle");
                    setRecipientMessage("");
                    setResolvedRecipientEmail("");
                    if (status !== "sending") {
                      setStatus("idle");
                      setStatusMessage("");
                    }
                  }}
                />
              </div>

              <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto xl:h-11 xl:flex-nowrap">
                <button
                  data-test="admin-crew-report-request-button"
                  className={twMerge(
                    adminStyles.button.primary,
                    adminStyles.button.safe,
                    adminStyles.button.disabled,
                    "h-11 w-full min-w-[155px] flex-none px-3 sm:w-auto",
                  )}
                  type="submit"
                  disabled={!canRequestCrewReport}
                >
                  <PaperAirplaneIcon className="h-5 w-5" />
                  <span className="grid">
                    <span aria-hidden="true" className="invisible col-start-1 row-start-1">
                      Request Crew Report
                    </span>
                    <span className="col-start-1 row-start-1 text-center">{requestButtonLabel}</span>
                  </span>
                </button>

                <button
                  data-test="admin-crew-report-upload-button"
                  className={twMerge(
                    adminStyles.button.primary,
                    adminStyles.button.disabled,
                    "h-11 w-full min-w-[155px] flex-none whitespace-nowrap border border-green bg-white px-3 text-green hover:bg-[#E9F4F3] dark:bg-gray-800 dark:hover:bg-gray-700 sm:w-[210px]",
                  )}
                  type="button"
                  disabled={isParsingUpload || isImportBusy || isRequestWorkflowBusy}
                  onClick={handleUploadButtonClick}
                >
                  <ArrowUpTrayIcon className="h-5 w-5" />
                  {isParsingUpload ? "Reading File" : "Upload Crew Report"}
                </button>

                {canImportCrewReports && (
                  <>
                    <button
                      data-test="admin-crew-report-import-button"
                      className={twMerge(
                        adminStyles.button.primary,
                        adminStyles.button.disabled,
                        "h-11 w-full min-w-[155px] flex-none whitespace-nowrap border border-green bg-white px-3 text-green hover:bg-[#E9F4F3] dark:bg-gray-800 dark:hover:bg-gray-700 sm:w-[210px]",
                      )}
                      type="button"
                      disabled={isImportBusy || isRequestWorkflowBusy || isParsingUpload || isSavingUpload}
                      onClick={handleImportPreview}
                    >
                      <ImportIcon className="h-5 w-5" aria-hidden="true" />
                      {isPreviewingImport ? "Checking" : isImporting ? "Importing" : "Import Crew Report"}
                    </button>

                    <label
                      htmlFor="admin-crew-report-initial-import-checkbox"
                      data-test="admin-crew-report-initial-import-option"
                      className="ml-1 flex min-w-[250px] max-w-[330px] flex-none cursor-pointer items-center gap-2 px-1 py-1 sm:min-h-11"
                    >
                      <AdminCrewReportCheckbox
                        id="admin-crew-report-initial-import-checkbox"
                        dataTest="admin-crew-report-initial-import-checkbox"
                        checked={allowTokenlessInitialImports}
                        disabled={isImportBusy || isRequestWorkflowBusy || isParsingUpload || isSavingUpload}
                        onChange={(event) => setAllowTokenlessInitialImports(event.target.checked)}
                      />
                      <span className="min-w-0">
                        <span className="block text-xs leading-4 text-[#65706F] dark:text-gray-400">
                          Broader import: include authenticated vessel reports with or without a matching Opslink
                          request reference, including reports received before Opslink sent a request.
                        </span>
                      </span>
                    </label>
                  </>
                )}

                <label className="sr-only" htmlFor="admin-crew-report-upload-input">
                  {uploadInputLabel}
                </label>
                <input
                  ref={uploadInputRef}
                  id="admin-crew-report-upload-input"
                  type="file"
                  accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  className="hidden"
                  data-test="admin-crew-report-upload-input"
                  aria-label={uploadInputLabel}
                  title={uploadInputLabel}
                  disabled={isImportBusy || isRequestWorkflowBusy}
                  onChange={handleUploadFileChange}
                />
              </div>
            </div>

            {vesselsStatus === "error" && (
              <p className="text-sm font-medium text-[#BF0002]" role="alert">
                {vesselLoadMessage}
              </p>
            )}

            {selectedVessel && (
              <div
                data-test="admin-crew-report-selected-vessel"
                className="grid gap-3 bg-[#F1FAF8] p-4 text-sm dark:bg-teal-950/30 sm:grid-cols-4"
              >
                <div>
                  <div className="text-xs font-semibold uppercase text-[#6B7473] dark:text-gray-400">Vessel</div>
                  <div className="break-words font-semibold">{selectedVessel.vesselName}</div>
                </div>
                <div>
                  <div className="text-xs font-semibold uppercase text-[#6B7473] dark:text-gray-400">Code</div>
                  <div className="break-words">{selectedVessel.vesselCode}</div>
                </div>
                <div>
                  <div className="text-xs font-semibold uppercase text-[#6B7473] dark:text-gray-400">IMO</div>
                  <div className="break-words">{selectedVessel.imoNumber}</div>
                </div>
                <div>
                  <div className="text-xs font-semibold uppercase text-[#6B7473] dark:text-gray-400">Type</div>
                  <div className="break-words">{selectedVessel.vesselType || "Not available"}</div>
                </div>
              </div>
            )}

            {recipientMessage && recipientStatus === "error" ? (
              <p
                data-test="admin-crew-report-recipient-status"
                className={recipientStatusMessageClassName}
                role="alert"
                aria-live="polite"
              >
                {recipientMessage}
              </p>
            ) : recipientMessage ? (
              <p
                data-test="admin-crew-report-recipient-status"
                className={recipientStatusMessageClassName}
                role="status"
                aria-live="polite"
              >
                {recipientMessage}
              </p>
            ) : null}

            {uploadStatusMessage && uploadStatus === "error" ? (
              <p
                data-test="admin-crew-report-upload-status"
                className={uploadStatusMessageClassName}
                role="alert"
                aria-live="polite"
              >
                {uploadStatusMessage}
              </p>
            ) : uploadStatusMessage ? (
              <p
                data-test="admin-crew-report-upload-status"
                className={uploadStatusMessageClassName}
                role="status"
                aria-live="polite"
              >
                {uploadStatusMessage}
              </p>
            ) : null}

            {statusMessage && status === "error" ? (
              <p
                data-test="admin-crew-report-status"
                className={requestStatusMessageClassName}
                role="alert"
                aria-live="polite"
              >
                {statusMessage}
              </p>
            ) : statusMessage ? (
              <p
                data-test="admin-crew-report-status"
                className={requestStatusMessageClassName}
                role="status"
                aria-live="polite"
              >
                {statusMessage}
              </p>
            ) : null}

            {canImportCrewReports && importStatusMessage && importStatus === "error" ? (
              <p
                data-test="admin-crew-report-import-status"
                className={importStatusMessageClassName}
                role="alert"
                aria-live="polite"
              >
                {importStatusMessage}
              </p>
            ) : canImportCrewReports && importStatusMessage ? (
              <p
                data-test="admin-crew-report-import-status"
                className={importStatusMessageClassName}
                role="status"
                aria-live="polite"
              >
                {importStatusMessage}
              </p>
            ) : null}
          </form>

          {!isInitialStatisticsLoading && (
            <>
              <AdminCrewReportStatisticsGrid
                status={statisticsStatus}
                statistics={statistics}
                errorMessage={statisticsErrorMessage}
              />

              <AdminCrewReportRunningVesselsTable
                status={statisticsStatus}
                statistics={statistics?.runningVesselCrewReports ?? null}
                errorMessage={statisticsErrorMessage}
                requestMessage={tableRequestMessage}
                requestMessageStatus={tableRequestMessageStatus}
                activeRequestImoNumber={activeTableRequestImoNumber}
                reportActionsDisabled={tableReportActionsDisabled}
                requestActionsDisabled={areStatisticsRequestActionsStale}
                requestActionBusy={tableRequestActionBusy}
                canRequestReports={canUseOperationalActions}
                canBulkRequestReports={canUseBulkRequests}
                bulkRequestControlsDisabled={tableBulkRequestControlsDisabled}
                selectedBulkRequestImoNumbers={selectedBulkRequestImoNumbers}
                onShowReport={handleTableShowReport}
                onRequestReport={handleTableRequestReport}
                onBulkRequestSelectionChange={setSelectedBulkRequestImoNumbers}
                onBulkRequest={bulkRequests.openConfirmation}
              />
            </>
          )}

          {canUseOperationalActions && requestConfirmationTarget && (
            <AdminCrewReportRequestConfirmationModal
              vesselName={requestConfirmationTarget.vessel.vesselName}
              recipientEmail={requestConfirmationTarget.recipientEmail}
              onClose={closeRequestConfirmation}
              onConfirm={handleConfirmCrewReportRequest}
            />
          )}

          {canUseBulkRequests && bulkRequests.confirmationVessels && (
            <AdminCrewReportBulkRequestConfirmationModal
              vessels={bulkRequests.confirmationVessels}
              onClose={bulkRequests.closeConfirmation}
              onConfirm={bulkRequests.start}
            />
          )}

          {canUseBulkRequests && bulkRequests.isStatusModalOpen && (
            <AdminCrewReportBulkRequestStatusModal
              items={bulkRequests.items}
              runStatus={bulkRequests.runStatus}
              isUpdatingStatuses={isBulkStatusRefreshPending}
              onClose={bulkRequests.requestClose}
              onStop={bulkRequests.stop}
              onResume={bulkRequests.resume}
            />
          )}

          {viewReportVessel && (
            <AdminCrewReportViewModal vessel={viewReportVessel} onClose={() => setViewReportVessel(null)} />
          )}

          {canUseOperationalActions && uploadPreview && (
            <AdminCrewReportUploadModal
              preview={uploadPreview}
              fileVesselImo={uploadFileVesselImo}
              vesselDetailsStatus={uploadVesselDetailsStatus}
              vesselDetails={uploadVesselDetails}
              isSaving={isSavingUpload}
              submitDisabledReason={uploadSubmitDisabledReason}
              submitMessage={uploadSubmitMessage}
              submitMessageDetails={uploadSubmitMessageDetails}
              submitMessageTone={uploadSubmitStatus === "error" || uploadSubmitDisabledReason ? "error" : "neutral"}
              onCancel={closeUploadPreview}
              onRowsChange={handleUploadRowsChange}
              onSubmit={handleUploadSubmit}
            />
          )}

          {canImportCrewReports && importPreview && (
            <AdminCrewReportImportConfirmationModal
              preview={importPreview}
              onClose={closeImportPreview}
              onConfirm={handleImportCrewReports}
            />
          )}

          {canImportCrewReports && importRunResult && isImportModalOpen && (
            <AdminCrewReportImportModal
              result={importRunResult}
              onClose={() => setIsImportModalOpen(false)}
              onExport={() => printAdminCrewReportImportRunReport(importRunResult)}
            />
          )}
        </div>
      </div>
    </main>
  );

  return (
    <>
      {isInitialStatisticsLoading && (
        <span className="sr-only" role="status" aria-live="polite">
          Loading Crew Report information.
        </span>
      )}
      {busyPage}
    </>
  );
};

export default AdminCrewReportPanel;
